import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HUB_ORIGIN = '50% 50%';

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	const visual = section.querySelector('.js-about-visual');
	if (!visual) {
		return null;
	}

	const orbits = visual.querySelectorAll('.about__orbit');
	const hubLogo = visual.querySelector('.about__hub-logo');
	const introBits = section.querySelectorAll(
		'.about__copy .text-h2, .about__body, .about__author, .about__cta',
	);

	const orbitTargets = [...orbits];

	gsap.set(orbitTargets, { transformOrigin: HUB_ORIGIN, scale: 0.72, autoAlpha: 0 });
	if (hubLogo) {
		gsap.set(hubLogo, { y: 10, autoAlpha: 0 });
	}
	if (introBits.length) {
		gsap.set(introBits, { y: 20, autoAlpha: 0 });
	}

	let idleTimeline = null;

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: 'top 78%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
		onComplete: () => {
			const outerOrbit = visual.querySelector('.about__orbit--outer');
			if (outerOrbit) {
				idleTimeline = gsap.timeline({
					repeat: -1,
					yoyo: true,
					defaults: { ease: 'sine.inOut' },
				});
				// Scale only — opacity on the whole ring hides border tweaks on --color-surface
				idleTimeline.to(outerOrbit, { scale: 1.025, duration: 3.4 }, 0);
			}
		},
	});

	if (introBits.length) {
		entrance.to(introBits, { y: 0, autoAlpha: 1, duration: 0.65, stagger: 0.08 }, 0);
	}

	entrance
		.to(
			visual.querySelector('.about__orbit--outer'),
			{ scale: 1, autoAlpha: 1, duration: 0.95, ease: 'back.out(1.35)' },
			0.26,
		)
		.to(
			visual.querySelector('.about__orbit--inner'),
			{ scale: 1, autoAlpha: 1, duration: 0.9, ease: 'back.out(1.5)' },
			0.4,
		);

	if (hubLogo) {
		entrance.to(hubLogo, { y: 0, autoAlpha: 1, duration: 0.55 }, 0.58);
	}

	return () => {
		entrance.scrollTrigger?.kill();
		idleTimeline?.kill();
		gsap.killTweensOf([...orbitTargets, hubLogo, ...introBits].filter(Boolean));
		gsap.set([...orbitTargets, hubLogo, ...introBits].filter(Boolean), {
			clearProps: 'all',
		});
	};
};

/**
 * @param {HTMLElement} section
 */
const bindSectionReduced = (section) => {
	const visual = section.querySelector('.js-about-visual');
	const introBits = section.querySelectorAll(
		'.about__copy, .about__author-block, .about__cta',
	);
	if (!visual) {
		return null;
	}

	const targets = [visual, ...introBits];
	gsap.set(targets, { autoAlpha: 0 });

	const tween = gsap.to(targets, {
		autoAlpha: 1,
		duration: 0.5,
		stagger: 0.06,
		ease: 'power2.out',
		scrollTrigger: {
			trigger: section,
			start: 'top 85%',
			once: true,
		},
	});

	return () => {
		tween.scrollTrigger?.kill();
		gsap.killTweensOf(targets);
		gsap.set(targets, { clearProps: 'all' });
	};
};

export const initAboutOrbitAnimation = () => {
	const sections = document.querySelectorAll('.section--about');
	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (min-width: 640px)', () => {
		sections.forEach((section) => {
			const cleanup = bindSection(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});

	gsap.matchMedia().add('(prefers-reduced-motion: reduce)', () => {
		sections.forEach((section) => {
			const cleanup = bindSectionReduced(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
